import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import { PORTFOLIO, PORTFOLIO_MODAL } from "@/constants/testIds";

export default function PortfolioGrid({ projects }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const isOpen = activeIndex !== null;
  const active = isOpen ? projects[activeIndex] : null;

  const open = (i) => setActiveIndex(i);
  const close = useCallback(() => setActiveIndex(null), []);
  const prev = useCallback(
    () => setActiveIndex((i) => (i === null ? null : (i - 1 + projects.length) % projects.length)),
    [projects.length]
  );
  const next = useCallback(
    () => setActiveIndex((i) => (i === null ? null : (i + 1) % projects.length)),
    [projects.length]
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, close, prev, next]);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[260px] gap-4 md:gap-5">
        {projects.map((p, i) => (
          <motion.button
            key={p.key}
            type="button"
            onClick={() => open(i)}
            data-testid={PORTFOLIO.card(p.key)}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: (i % 4) * 0.06 }}
            className={`group relative overflow-hidden bg-[#0a0a0a] border border-white/5 hover:border-cyan-500/60 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_20px_rgba(34,211,238,0.35)] transition-[border-color,box-shadow] duration-300 hud-corners text-left ${p.span || ""}`}
            aria-label={`Open ${p.title} details`}
          >
            <span className="hud-c-tl" />
            <span className="hud-c-br" />

            <div className="absolute inset-0">
              <img
                src={p.image}
                alt={p.title}
                loading="lazy"
                className="w-full h-full object-cover opacity-60 grayscale group-hover:opacity-90 group-hover:grayscale-0 group-hover:scale-105 transition-[opacity,transform,filter] duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
            </div>

            <div className="relative h-full flex flex-col justify-between p-5 md:p-6 z-10">
              <div className="flex items-start justify-between">
                <span className="tag-pill">{p.year}</span>
                <span className="w-9 h-9 flex items-center justify-center border border-cyan-500/40 text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ArrowUpRight size={16} />
                </span>
              </div>
              <div>
                <div className="font-hud text-cyan-400/80 mb-2">{p.category}</div>
                <h3 className="font-mono-display text-lg sm:text-xl md:text-2xl uppercase tracking-tight text-slate-100 leading-tight">
                  {p.title}
                </h3>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {p.tags.map((t) => (
                    <span key={t} className="tag-pill">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {isOpen && active && (
          <motion.div
            data-testid={PORTFOLIO_MODAL.root}
            role="dialog"
            aria-modal="true"
            aria-labelledby="portfolio-modal-title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 md:p-10"
          >
            <button
              type="button"
              aria-label="Close"
              onClick={close}
              className="absolute inset-0 bg-black/85"
              style={{ backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
            />

            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 12 }}
              transition={{ duration: 0.25 }}
              className="relative w-full max-w-6xl bg-[#0a0a0a] border border-cyan-500/40 shadow-[0_0_60px_rgba(34,211,238,0.15)] hud-corners"
            >
              <span className="hud-c-tl" />
              <span className="hud-c-br" />

              <button
                type="button"
                onClick={close}
                data-testid={PORTFOLIO_MODAL.close}
                aria-label="Close dialog"
                className="absolute top-3 right-3 z-20 w-10 h-10 flex items-center justify-center border border-cyan-500/40 bg-[#0a0a0a] text-cyan-300 hover:text-cyan-200 hover:border-cyan-400 transition-colors"
              >
                <X size={18} />
              </button>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                <div className="lg:col-span-7 relative bg-[#050505] flex items-center justify-center min-h-[260px] sm:min-h-[340px] lg:min-h-[520px]">
                  <img
                    key={active.image}
                    src={active.image}
                    alt={active.title}
                    data-testid={PORTFOLIO_MODAL.image}
                    className="max-h-[70vh] w-full object-contain"
                    loading="eager"
                  />
                  <div className="absolute bottom-3 left-3 tag-pill" data-testid={PORTFOLIO_MODAL.counter}>
                    {activeIndex + 1} / {projects.length}
                  </div>
                </div>

                <div className="lg:col-span-5 p-6 md:p-8 flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <span className="tag-pill">{active.year}</span>
                    <span className="font-hud text-cyan-400/80">{active.category}</span>
                  </div>

                  <h3
                    id="portfolio-modal-title"
                    data-testid={PORTFOLIO_MODAL.title}
                    className="font-mono-display text-2xl md:text-3xl uppercase tracking-tight text-slate-100 leading-tight"
                  >
                    {active.title}
                  </h3>

                  <p
                    data-testid={PORTFOLIO_MODAL.description}
                    className="text-slate-300 font-body leading-relaxed"
                  >
                    {active.description}
                  </p>

                  <div className="flex flex-wrap gap-1.5">
                    {active.tags.map((t) => (
                      <span key={t} className="tag-pill">{t}</span>
                    ))}
                  </div>

                  <div className="mt-auto pt-4 flex items-center justify-between border-t border-white/5">
                    <button
                      type="button"
                      onClick={prev}
                      data-testid={PORTFOLIO_MODAL.prev}
                      className="btn-ghost"
                      aria-label="Previous project"
                    >
                      <ChevronLeft size={14} /> Prev
                    </button>
                    <span className="font-hud text-slate-500 hidden sm:inline">use ← / → to navigate</span>
                    <button
                      type="button"
                      onClick={next}
                      data-testid={PORTFOLIO_MODAL.next}
                      className="btn-neon"
                      aria-label="Next project"
                    >
                      Next <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
